import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { ISnowComputerApplicationsState } from './snowComputerApplications.model';
import {
  deleteSnowComputerApplications,
  getSnowComputerApplicationsById,
  saveSnowComputerApplications,
  searchSnowComputerApplications,
} from './snowComputerApplications.action';
import { ISnowComputerApplications } from '../../../services/snow/snowComputerApplications/snowComputerApplications.model';

export const initialState: ISnowComputerApplicationsState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const snowComputerApplicationsSlice = createSlice({
  name: 'snowComputerApplications',
  initialState,
  reducers: {
    clearSnowComputerApplications: () => {
      return initialState;
    },
    clearSnowComputerApplicationsMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearSnowComputerApplicationsGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchSnowComputerApplications.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchSnowComputerApplications.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchSnowComputerApplications.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getSnowComputerApplicationsById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getSnowComputerApplicationsById.fulfilled.type]: (
      state,
      action: PayloadAction<ISnowComputerApplications>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getSnowComputerApplicationsById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveSnowComputerApplications.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveSnowComputerApplications.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveSnowComputerApplications.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteSnowComputerApplications.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteSnowComputerApplications.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteSnowComputerApplications.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const snowComputerApplicationsSelector = (state: RootState) => state.snowComputerApplications;

// Actions
export const {
  clearSnowComputerApplications,
  clearSnowComputerApplicationsMessages,
  clearSnowComputerApplicationsGetById,
  setTableColumnSelection,
} = snowComputerApplicationsSlice.actions;

// The reducer
export default snowComputerApplicationsSlice.reducer;
